import React from 'react';

/**
 * ErrorBoundary — catches React DOM reconciliation errors caused by:
 * - Google Translate modifying text nodes in the DOM
 * - GTM injecting/moving script nodes that React tracks
 * - react-helmet managing <head> nodes simultaneously with external scripts
 *
 * On catching a "removeChild" type error, it attempts a one-time page reload
 * to recover gracefully instead of showing a blank screen.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    const isDomReconciliationError =
      error?.message?.includes('removeChild') ||
      error?.message?.includes('insertBefore') ||
      error?.message?.includes('not a child of this node');

    if (isDomReconciliationError) {
      // This is a known React + GTM/Google Translate conflict.
      // A single reload fixes it by re-syncing the DOM.
      const reloadKey = '_btc_reload_attempted';
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, '1');
        window.location.reload();
        return;
      }
    }

    console.error('[ErrorBoundary] Uncaught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Fallback UI — shown only if reload also fails (very rare)
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          fontFamily: 'sans-serif',
          padding: '2rem',
          textAlign: 'center'
        }}>
          <h2 style={{ marginBottom: '1rem' }}>Something went wrong.</h2>
          <p style={{ color: '#666', marginBottom: '1.5rem' }}>
            Please try refreshing the page.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '0.75rem 2rem',
              background: '#163435',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
